import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../config/api';

const GestionColegios = () => {
  const [colegios, setColegios] = useState([]);
  const [nuevoColegio, setNuevoColegio] = useState({
    nombre: '',
    ruc: '',
    ciudad: '',
    departamento: '',
    telefono: '',
    email: '',
    director: '',
    director_2: '',
    director_3: '',
    coordinador_1: '',
    coordinador_2: '',
  });

  const [editandoId, setEditandoId] = useState(null);

  // ✅ Modal de vista (solo lectura)
  const [modalAbierto, setModalAbierto] = useState(false);
  const [colegioVista, setColegioVista] = useState(null);

  // ✅ Buscador
  const [busqueda, setBusqueda] = useState('');

  const resetFormulario = () => {
    setNuevoColegio({
      nombre: '',
      ruc: '',
      ciudad: '',
      departamento: '',
      telefono: '',
      email: '',
      director: '',
      director_2: '',
      director_3: '',
      coordinador_1: '',
      coordinador_2: '',
    });
  };

  const cargarColegios = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/colegios`);
      if (!res.ok) throw new Error('No se pudo cargar colegios');
      const data = await res.json();
      setColegios(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error cargando colegios:', error);
      setColegios([]);
    }
  };

  useEffect(() => {
    cargarColegios();
  }, []);

  // ✅ Cerrar modal con ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setModalAbierto(false);
        setColegioVista(null);
      }
    };
    if (modalAbierto) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalAbierto]);

  const manejarCambio = (e) => {
    setNuevoColegio({ ...nuevoColegio, [e.target.name]: e.target.value });
  };

  const manejarEnvio = async (e) => {
    e.preventDefault();

    const url = editandoId
      ? `${API_BASE}/api/colegios/${editandoId}`
      : `${API_BASE}/api/colegios`;

    const metodo = editandoId ? 'PUT' : 'POST';

    try {
      const respuesta = await fetch(url, {
        method: metodo,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nuevoColegio),
      });

      if (respuesta.ok) {
        await cargarColegios();
        resetFormulario();
        setEditandoId(null);
      } else {
        let msg = 'Error al registrar/actualizar colegio';
        try {
          const data = await respuesta.json();
          if (data?.mensaje) msg = data.mensaje;
        } catch {}
        alert(msg);
      }
    } catch (error) {
      console.error('Error registrando/actualizando colegio:', error);
      alert('Error de conexión al registrar/actualizar colegio');
    }
  };

  const editarColegio = (colegio) => {
    setNuevoColegio({
      nombre: colegio.nombre || '',
      ruc: colegio.ruc || '',
      ciudad: colegio.ciudad || '',
      departamento: colegio.departamento || '',
      telefono: colegio.telefono || '',
      email: colegio.email || '',
      director: colegio.director || '',
      director_2: colegio.director_2 || '',
      director_3: colegio.director_3 || '',
      coordinador_1: colegio.coordinador_1 || '',
      coordinador_2: colegio.coordinador_2 || '',
    });
    setEditandoId(colegio.id);
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    resetFormulario();
  };

  const eliminarColegio = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este colegio?')) return;

    try {
      const res = await fetch(`${API_BASE}/api/colegios/${id}`, {
        method: 'DELETE',
      });

      if (res.ok) {
        await cargarColegios();
      } else {
        alert('No se pudo eliminar el colegio');
      }
    } catch (error) {
      console.error('Error eliminando colegio:', error);
      alert('Error de conexión al eliminar el colegio');
    }
  };

  // ✅ Abrir modal de vista (no toca formulario)
  const verColegio = (colegio) => {
    setColegioVista(colegio);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setColegioVista(null);
  };

  const renderFila = (label, value) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b">
      <div className="text-sm font-semibold text-gray-700">{label}</div>
      <div className="md:col-span-2 text-sm text-gray-800 break-words">
        {value && String(value).trim() !== '' ? value : <span className="text-gray-400">-</span>}
      </div>
    </div>
  );

  const colegiosFiltrados = useMemo(() => {
    const q = (busqueda || '').trim().toLowerCase();
    if (!q) return colegios;
    return (colegios || []).filter((c) => (c.nombre || '').toLowerCase().includes(q));
  }, [colegios, busqueda]);

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-blue-700 mb-4">Gestión de Colegios</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ============================= */}
        {/* SECCIÓN 1: REGISTRAR / EDITAR */}
        {/* ============================= */}
        <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {editandoId ? 'Editar colegio' : 'Registrar nuevo colegio'}
            </h3>

            {editandoId && (
              <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800">
                Modo edición
              </span>
            )}
          </div>

          {editandoId && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded flex items-center justify-between">
              <div className="text-sm">Estás editando un colegio.</div>
              <button
                type="button"
                onClick={cancelarEdicion}
                className="bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 rounded"
              >
                Cancelar edición
              </button>
            </div>
          )}

          <form onSubmit={manejarEnvio} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              name="nombre"
              placeholder="Nombre"
              value={nuevoColegio.nombre}
              onChange={manejarCambio}
              className="border p-2 rounded md:col-span-2"
              required
            />

            <input
              name="ruc"
              placeholder="RUC"
              value={nuevoColegio.ruc}
              onChange={manejarCambio}
              className="border p-2 rounded"
            />

            <input
              name="telefono"
              placeholder="Teléfono"
              value={nuevoColegio.telefono}
              onChange={manejarCambio}
              className="border p-2 rounded"
            />

            <input
              name="ciudad"
              placeholder="Ciudad"
              value={nuevoColegio.ciudad}
              onChange={manejarCambio}
              className="border p-2 rounded"
            />

            <input
              name="departamento"
              placeholder="Departamento"
              value={nuevoColegio.departamento}
              onChange={manejarCambio}
              className="border p-2 rounded"
            />

            <input
              name="email"
              placeholder="Email"
              value={nuevoColegio.email}
              onChange={manejarCambio}
              className="border p-2 rounded md:col-span-2"
            />

            <input
              name="director"
              placeholder="Director"
              value={nuevoColegio.director}
              onChange={manejarCambio}
              className="border p-2 rounded"
            />

            <input
              name="director_2"
              placeholder="Director 2"
              value={nuevoColegio.director_2}
              onChange={manejarCambio}
              className="border p-2 rounded"
            />

            <input
              name="director_3"
              placeholder="Director 3"
              value={nuevoColegio.director_3}
              onChange={manejarCambio}
              className="border p-2 rounded"
            />

            <input
              name="coordinador_1"
              placeholder="Coordinador 1"
              value={nuevoColegio.coordinador_1}
              onChange={manejarCambio}
              className="border p-2 rounded"
            />

            <input
              name="coordinador_2"
              placeholder="Coordinador 2"
              value={nuevoColegio.coordinador_2}
              onChange={manejarCambio}
              className="border p-2 rounded"
            />

            <button
              type="submit"
              className="md:col-span-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
            >
              {editandoId ? 'Guardar cambios' : 'Registrar colegio'}
            </button>

            {!editandoId && (
              <button
                type="button"
                onClick={resetFormulario}
                className="md:col-span-2 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
              >
                Limpiar
              </button>
            )}
          </form>
        </div>

        {/* ============================= */}
        {/* SECCIÓN 2: COLEGIOS REGISTRADOS */}
        {/* ============================= */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-semibold text-gray-800">Colegios registrados</h3>
              <span className="text-sm text-gray-500">{colegiosFiltrados.length}</span>
            </div>

            {/* ✅ Buscador con lupa */}
            <div className="relative w-full max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar colegio..."
                className="w-full border rounded pl-9 pr-3 py-2 text-sm"
              />
            </div>
          </div>

          <ul className="divide-y border rounded">
            {colegiosFiltrados.map((colegio) => (
              <li
                key={colegio.id}
                className="p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <div>
                  <p className="font-semibold">{colegio.nombre}</p>
                  <p className="text-sm text-gray-500">
                    {colegio.ciudad || '-'}{colegio.departamento ? `, ${colegio.departamento}` : ''}
                  </p>
                </div>
                <div className="space-x-2">
                  <button
                    title="Ver datos"
                    onClick={() => verColegio(colegio)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                  >
                    👁️
                  </button>

                  <button
                    title="Modificar"
                    onClick={() => editarColegio(colegio)}
                    className="bg-yellow-400 hover:bg-yellow-500 text-white px-3 py-1 rounded"
                  >
                    ✏️
                  </button>

                  <button
                    title="Eliminar"
                    onClick={() => eliminarColegio(colegio.id)}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                  >
                    🗑️
                  </button>
                </div>
              </li>
            ))}

            {colegiosFiltrados.length === 0 && (
              <li className="p-4 text-sm text-gray-500">No se encontraron colegios con ese criterio.</li>
            )}
          </ul>
        </div>
      </div>

      {/* ✅ MODAL VER COLEGIO (SOLO LECTURA) */}
      {modalAbierto && colegioVista && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4"
          onClick={cerrarModal}
        >
          <div
            className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <div>
                <h3 className="text-lg font-bold">Datos del colegio</h3>
                <p className="text-sm opacity-90">
                  <span className="font-semibold">{colegioVista.nombre}</span>
                </p>
              </div>
              <button
                onClick={cerrarModal}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded"
                title="Cerrar"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              <div className="space-y-1">
                {renderFila('Nombre', colegioVista.nombre)}
                {renderFila('RUC', colegioVista.ruc)}
                {renderFila('Ciudad', colegioVista.ciudad)}
                {renderFila('Departamento', colegioVista.departamento)}
                {renderFila('Teléfono', colegioVista.telefono)}
                {renderFila('Email', colegioVista.email)}
                {renderFila('Director', colegioVista.director)}
                {renderFila('Director 2', colegioVista.director_2)}
                {renderFila('Director 3', colegioVista.director_3)}
                {renderFila('Coordinador 1', colegioVista.coordinador_1)}
                {renderFila('Coordinador 2', colegioVista.coordinador_2)}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={cerrarModal}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  Cerrar
                </button>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                Tip: también podés cerrar con la tecla <span className="font-semibold">ESC</span> o clic fuera.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionColegios;